import { inject, Injectable, Signal } from '@angular/core';
import { combineLatest, fromEvent, Observable, of, switchMap, timer } from 'rxjs';
import { Router } from '@angular/router';
import { CommentBase, FilterEnum, User, ViewEnum } from '@shared/api/api-loop/models';
import {
  CommentBaseModel,
  CommentChatModel,
  CommentMailModel,
  isChatComment,
  isMailComment
} from '../../../shared/models-api-loop/comment/comment.model';
import { GroupModel } from '../../../shared/models-api-loop/contact/contact.model';
import { INotificationSettings } from '../../../../shared/modules/main/user-settings/general/general.component';
import { bufferTime, catchError, concatMap, filter, map, retry, take, tap } from 'rxjs/operators';
import { ElectronService } from '@shared/services/electron/electron';
import { IPC } from '../../../shared/communication/ipc-constants';
import { StorageKey, StorageService } from '../../../shared/services/storage/storage.service';
import { checkIfOS, isFirefox, OperatingSystem } from '../../../shared/utils/common-utils';
import { ContactAvatarsApiCacheService } from '@shared/modules/files/shell/contact-avatars-api-cache/contact-avatars-api-cache.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CommentActionsService } from '@shared/modules/comments/components/services/comment-actions/comment-actions.service';
import { differenceInMinutes } from 'date-fns';
import { SmartConversationsApiService } from '@shared/modules/conversations/services/conversations-api-cache/smart-conversations-api.service';
import { shareUntilDestroyed } from '@shared/utils/rxjs/rxjs-angular-interop/share-until-destroyed';
import { isGroupContact } from '@shared/modules/contacts/common/helpers/is-group-contact';
import { createChatCardId } from '@shared/modules/conversations/common/helpers/create-chat-card-id';
import { ConversationView } from '@shared/modules/conversations/common/constants/conversation-view';
import { CONVERSATION_VIEW_ROUTE } from '@shared/modules/conversations/common/constants/conversation-view-route';
import { FILTER_ENUM_PREFIX } from '@shared/modules/shared-tag/common/helpers/shared-tag-folder-url';
import { UserAuthService } from '@shared/modules/auth/auth-data-access/user-auth.service';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { UsersInLoopService } from '@shared/modules/user-manager/data-access/users-in-loop.service';

@UntilDestroy()
@Injectable()
export class DesktopNotificationsService {
  private _router: Router = inject(Router);
  private _storageService: StorageService = inject(StorageService);
  private _electronService: ElectronService = inject(ElectronService);
  private contactAvatarsApiCacheService: ContactAvatarsApiCacheService = inject(ContactAvatarsApiCacheService);
  private smartConversationsApiService: SmartConversationsApiService = inject(SmartConversationsApiService);
  private readonly userAuthService: UserAuthService = inject(UserAuthService);
  private readonly usersInLoopService: UsersInLoopService = inject(UsersInLoopService);
  private readonly commentActionsService: CommentActionsService = inject(CommentActionsService);

  private readonly userSignal: Signal<User | null> = toSignal(
    this.userAuthService.currentSession$.pipe(map(session => session.user)),
    { initialValue: null }
  );

  protected readonly currentUserAvatar$: Observable<any> = toObservable(this.userSignal).pipe(
    switchMap(user => {
      return user
        ? this.contactAvatarsApiCacheService.getContactUrlLink$(user)
        : of('/shared/assets/graphics/icons/favicon/icon.ico');
    }),
    shareUntilDestroyed()
  );

  subscribeToDesktopNotifications() {
    combineLatest([
      this.commentActionsService.commentsCreatedWithSync$,
      this.usersInLoopService.getAllUsers$().pipe(map(users => users.map(user => user.email)))
    ])
      .pipe(
        map(([comments, userEmails]) => {
          return comments.filter(comment => {
            if (isMailComment(comment) && !comment.parent.id.includes('copy1T')) {
              return false;
            }

            // If post from notification is older than 30 minutes don't show the notification
            const diff = differenceInMinutes(new Date(), new Date(comment.created));
            if (diff > 30) {
              return false;
            }

            // If sender of the post from notification is currently signed in don't show the notification
            return userEmails.every((userEmail: string) => comment.author.email !== userEmail);
          });
        }),
        bufferTime(4 * 1000),
        map(comments => {
          return [...comments.flat()];
        }),
        filter(comments => {
          return !!comments.length;
        }),
        /**
         * Filter comments for notifications
         */
        concatMap((comments: CommentBase[]) => {
          return combineLatest([this.currentUserAvatar$, this.filterCommentsForNotifications(comments)]).pipe(take(1));
        }),
        map(([contactUrlLink, comments]) => {
          const currentUser = this.userSignal();
          return comments.map(commentBase => {
            const comment = CommentBaseModel.create(commentBase);
            const groupRecipient =
              comment instanceof CommentChatModel
                ? comment.shareList.resources.find(contact => isGroupContact(contact))
                : comment instanceof CommentMailModel
                  ? comment.to.resources.find(contact => isGroupContact(contact))
                  : null;

            return {
              Title: comment.author.name,
              Body:
                comment.snippet && comment.snippet.length > 80
                  ? comment.snippet.slice(0, 77) + '...'
                  : comment.snippet || '',
              Subject: comment.parent.name,
              Uri: this.generateUri(comment, currentUser.id),
              UserEmail: currentUser.email,
              IconUri: contactUrlLink,
              IsChat: comment instanceof CommentChatModel,
              IsGroup: groupRecipient != null,
              GroupName: groupRecipient == null ? undefined : groupRecipient.name,
              HasAttachments: comment.hasAttachments(),
              HasMention: comment.hasMention()
            } satisfies IDesktopNotificationOptions;
          });
        }),
        map(notifications => notifications.flat()),
        filter(notifications => {
          return notifications && notifications.length > 0;
        }),
        tap((notifications: IDesktopNotificationOptions[]) => {
          let [firstNotification, ...otherNotifications] = notifications;

          this.newNotification({
            ...firstNotification,
            ...{
              Title: otherNotifications.length ? notifications.length + ' new notifications' : firstNotification.Title
            }
          });
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private filterCommentsForNotifications(comments: CommentBase[]): Observable<CommentBase[]> {
    const parentIds = new Set(comments.map(comment => comment.parent.id.replace('-copy1T', '')));
    if (!parentIds.size) {
      return of([]);
    }
    return this.smartConversationsApiService.getByIds$([...parentIds]).pipe(
      map(response => {
        if (response.length !== parentIds.size) {
          const removeConversationIds = [...parentIds].filter(parentId =>
            response.some(conversation => conversation.id === parentId)
          );
          removeConversationIds.forEach(conversationId => {
            this.smartConversationsApiService.removeConversationFromStorage(conversationId);
          });
          throw new Error('Conversations not found');
        }
        return response;
      }),
      retry({
        count: 3,
        delay: (error, retryCount) => {
          return timer(retryCount * 1000);
        }
      }),
      catchError(err => {
        return of([]);
      }),
      map(conversations => {
        return conversations.filter(conversation => {
          return !!conversation && conversation.shouldShowInSubView(ViewEnum.LOOP_INBOX, FilterEnum.INBOX);
        });
      }),
      map(conversations => {
        return comments.filter(comment => {
          return conversations.some(conversation => {
            return conversation.id === comment.parent.id.replace('-copy1T', '');
          });
        });
      })
    );
  }

  private generateUri(comment: CommentBaseModel, userId: string): string {
    if (isChatComment(comment)) {
      // Find group in shareList
      let chatId = comment.shareList.resources.find(contact => contact?.$type === GroupModel.type)?.id;

      if (!chatId) {
        // Find other contact from 1on1 conversation if group is not in shareList
        const contactId = comment.shareList.resources.find(contact => contact.id !== userId)?.id;
        chatId = createChatCardId(this.userSignal()?.id, contactId);
      }

      return `${CONVERSATION_VIEW_ROUTE}/chat/${chatId}`;
    } else {
      // Loops and Emails are opened in My loop inbox
      return `${CONVERSATION_VIEW_ROUTE}/${ConversationView.loopInbox}/${FILTER_ENUM_PREFIX}${FilterEnum.INBOX}/${comment.parent.id.replace(
        '-copy1T',
        ''
      )}`;
    }
  }

  private navigateToUri(uri: string) {
    this._router.navigate([uri]);
  }

  private newNotification(options: IDesktopNotificationOptions) {
    // Get notification settings or use default
    let key = this._storageService.getKey(options.UserEmail, StorageKey.userNotificationSettings);
    let notificationSetting: INotificationSettings =
      this._storageService.getParsedItem(key) || new INotificationSettings();

    // Don't show notification when app is in focus
    if (!notificationSetting.isNotifyWhenFocusedEnabled && document.hasFocus()) {
      return;
    }

    // Not Allowed
    if (!notificationSetting.allowNotifications) {
      return;
    }
    // Chat
    if (options.IsChat && !notificationSetting.notifications.chat) {
      return;
    }
    // Email
    if (!options.IsChat && !options.IsGroup && !notificationSetting.notifications.email) {
      return;
    }
    // Team email
    if (options.IsGroup && !notificationSetting.notifications.teamEmail) {
      return;
    }

    // Build chat notification body
    if (options.IsChat) {
      if (options.HasAttachments) {
        options.Body = 'Message 📎\n' + options.Body;
      } else {
        options.Body = 'Message\n' + options.Body;
      }

      // Handle mention
      if (options.HasMention) {
        options.Body = '📢 ' + options.Body;
      }
    }

    // Build mail
    if (!options.IsChat) {
      if (options.HasAttachments) {
        options.Subject = options.Subject + ' 📎';
      }

      // For all emails we also show subject line
      options.Body = 'Subject: ' + options.Subject + '\n' + options.Body;
    }

    // If post is sent to team, there should be a name of the team in parentheses.
    if (options.IsGroup) {
      options.Title = options.Title + ' (' + options.GroupName + ')';
    }

    // Set silent option
    options.Silent = !notificationSetting?.notificationSound;

    // Build notification
    let notification = new Notification(options.Title, {
      body: options.Body,
      icon: options.IconUri,
      silent: true, // This does not work on Mac Firefox so we disable the custom sound
      requireInteraction: false
    });

    if (!options.Silent && !this.isFirefoxOnMac()) {
      let audio = new Audio('./shared/assets/sounds/pop.mp3');
      audio.play();
    }

    // Close notification automatically - prevents stacking many notifications
    setTimeout(() => {
      notification?.close();
    }, 5_000);

    fromEvent(notification, 'click')
      .pipe(take(1), untilDestroyed(this))
      .subscribe(() => {
        if (options.Uri) {
          this.navigateToUri(options.Uri);
        }

        this._electronService.ipcRenderer.send(IPC.FOCUS_ON_APP);
        notification.close();
      });
  }

  private isFirefoxOnMac(): boolean {
    return checkIfOS(OperatingSystem.DARWIN) && isFirefox();
  }
}

export interface IDesktopNotificationOptions {
  Title: string;
  Body: string;
  Uri: string;
  Silent?: boolean;
  UserEmail: string;
  IconUri: string;
  IsChat?: boolean;
  IsGroup?: boolean;
  Subject?: string;
  GroupName?: string;
  HasAttachments?: boolean;
  HasMention?: boolean;
}
